import type { Theme } from "@mui/material/styles";
import type { AppSx, ThemeStyleFactory } from "../types";

export type MdSnackbarPaperArgs = {
  bgWhite: boolean;
  color: string;
  darkMode: boolean;
};

export type MdSnackbarContentArgs = MdSnackbarPaperArgs;
export type MdSnackbarCloseIconArgs = {
  bgWhite: boolean;
  color: string;
  darkMode: boolean;
};

export interface AppLegacyWidgetStyles {
  simpleBlogCardBlur: ThemeStyleFactory<[imageUrl: string]>;
  cardFullHeight: ThemeStyleFactory<[shadow?: boolean]>;
  masterCardRoot: ThemeStyleFactory<[color: string]>;
  masterCardPattern: ThemeStyleFactory<[imageUrl: string]>;
  masterCardTitle: AppSx;
  projectCardRoot: AppSx;
  projectCardMedia: (theme: Theme) => AppSx;
  projectCardAvatar: (theme: Theme) => AppSx;
  reportsChartCard: AppSx;
  reportsChartLegendLabel: AppSx;
  mixedChartBox: ThemeStyleFactory<[height: number | string]>;
  progressLineChartLine: ThemeStyleFactory<[height: number | string]>;
  complexStatisticsCardClickable: AppSx;
  notificationMenuItem: AppSx;
  notificationItemContent: AppSx;
  notificationItemLabel: AppSx;
  mdSnackbarRoot: AppSx;
  mdSnackbarPaper: ThemeStyleFactory<[MdSnackbarPaperArgs]>;
  mdSnackbarContent: ThemeStyleFactory<[MdSnackbarContentArgs]>;
  mdSnackbarCloseIcon: ThemeStyleFactory<[MdSnackbarCloseIconArgs]>;
  mdSnackbarDivider: AppSx;
  mdPaginationList: AppSx;
}

export function buildLegacyWidgetStyles(theme: Theme): AppLegacyWidgetStyles {
  const { palette, functions, boxShadows, borders, typography, transitions } = theme as Theme & {
    functions: { linearGradient: (a: string, b: string) => string };
    boxShadows: { xl: string; md: string };
    borders: { borderWidth: string[]; borderRadius: { md: number | string } };
    typography: { fontWeightBold: number; size: { sm: string } };
    transitions: { create: (prop: string, opts: object) => string; easing: { easeInOut: string }; duration: { standard: number } };
  };

  const snackbarOnColorText = (colorName: string, bgWhite: boolean) => {
    if (bgWhite || colorName === "light") {
      return palette.text.main;
    }
    const tone = (palette as unknown as Record<string, { contrastText?: string }>)[colorName];
    return tone?.contrastText ?? palette.common.white;
  };

  return {
    simpleBlogCardBlur: (_theme, imageUrl) => ({
      backgroundImage: `url(${imageUrl})`,
      transform: "scale(0.94)",
      filter: "blur(12px)",
      backgroundSize: "cover",
    }),
    cardFullHeight: (_theme, shadow = true) => ({
      height: "100%",
      boxShadow: shadow ? undefined : "none",
    }),
    masterCardRoot: (_theme, color) => {
      const paletteAny = palette as unknown as Record<string, { main: string; state: string } | undefined> & {
        gradients?: Record<string, { main: string; state: string }>;
      };
      const gradients = paletteAny.gradients;
      const gradient = gradients?.[color] ?? gradients?.dark;
      return {
        background: gradient
          ? functions.linearGradient(gradient.main, gradient.state)
          : functions.linearGradient(gradients!.dark.main, gradients!.dark.state),
        boxShadow: boxShadows.xl,
        position: "relative",
      };
    },
    masterCardPattern: (_theme, imageUrl) => ({
      backgroundImage: `url(${imageUrl})`,
      backgroundSize: "cover",
    }),
    masterCardTitle: { mt: 3, mb: 5, pb: 1 },
    projectCardRoot: {
      display: "flex",
      flexDirection: "column",
      backgroundColor: "rgba(0,0,0,0)",
      boxShadow: "none",
      overflow: "visible",
    },
    projectCardMedia: (t) => ({
      maxWidth: "100%",
      margin: 0,
      boxShadow: (t as typeof theme & { boxShadows: { md: string } }).boxShadows.md,
      objectFit: "cover",
      objectPosition: "center",
    }),
    projectCardAvatar: (t) => ({
      border: `${(t as typeof theme & { borders: { borderWidth: string[] } }).borders.borderWidth[2]} solid ${t.palette.white.main}`,
      cursor: "pointer",
      position: "relative",
      ml: -1.25,
      "&:hover, &:focus": {
        zIndex: "10",
      },
    }),
    reportsChartCard: { height: "100%" },
    reportsChartLegendLabel: { mt: 0.15, mr: 0.5 },
    mixedChartBox: (_theme, height) => ({ height }),
    progressLineChartLine: (_theme, height) => ({ height }),
    complexStatisticsCardClickable: { cursor: "pointer" },
    notificationMenuItem: {
      display: "flex",
      alignItems: "center",
      width: "100%",
      color: palette.secondary.main,
      borderRadius: borders.borderRadius.md,
      transition: transitions.create("background-color", {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.standard,
      }),
      "& *": {
        transition: "color 100ms linear",
      },
      "&:not(:last-child)": {
        mb: 1,
      },
      "&:hover": {
        backgroundColor: (palette as typeof palette & { light: { main: string } }).light.main,
        "& *": {
          color: palette.dark.main,
        },
      },
    },
    notificationItemContent: {
      py: 0.5,
      display: "flex",
      alignItems: "center",
      lineHeight: 1,
    },
    notificationItemLabel: { ml: 1 },
    mdSnackbarRoot: {
      "& .MuiSnackbar-root": {
        position: "fixed !important",
        zIndex: 9999,
      },
      "&.MuiSnackbar-anchorOriginTopRight": {
        top: "24px !important",
        right: "24px !important",
      },
    },
    mdSnackbarPaper: (_theme, { bgWhite, color, darkMode }) => ({
      backgroundColor: (() => {
        if (bgWhite) return palette.white.main;
        if (color === "light") return darkMode ? palette.background.card : palette.white.main;
        return (palette as unknown as Record<string, { main?: string }>)[color]?.main || palette.error.main;
      })(),
      position: "relative",
      zIndex: 9999,
      "&:hover": {
        transform: "none",
      },
    }),
    mdSnackbarContent: (_theme, { bgWhite, color }) => ({
      fontSize: typography.size.sm,
      color: snackbarOnColorText(color, bgWhite),
    }),
    mdSnackbarCloseIcon: (_theme, { bgWhite, color, darkMode }) => ({
      color:
        bgWhite || color === "light"
          ? darkMode
            ? palette.text.primary
            : palette.dark.main
          : snackbarOnColorText(color, bgWhite),
      fontWeight: typography.fontWeightBold,
      cursor: "pointer",
      marginLeft: 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "&:hover": {
        opacity: 0.8,
      },
    }),
    mdSnackbarDivider: { margin: 0 },
    mdPaginationList: { listStyle: "none" },
  };
}
